import React, { useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { RankBar } from './reportUtils';
import { formatPercentCell } from './charts/chartDataHelpers';

const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

function normalizeForSort(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'boolean') return value ? 1 : 0;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2})?$/.test(raw)) {
    const ts = Date.parse(raw.length === 10 ? `${raw}T00:00:00` : raw.replace(' ', 'T'));
    if (!Number.isNaN(ts)) return ts;
  }

  if (/^-?\d+(?:[\.,]\d+)?$/.test(raw)) {
    const numeric = Number(raw.replace(',', '.'));
    if (!Number.isNaN(numeric)) return numeric;
  }

  return raw;
}

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  return collator.compare(String(a), String(b));
}

export default function StatisticalDataTable({
  columns,
  rows,
  rowKey,
  maxValueKey,
  loading,
  emptyLabel,
  loadingLabel,
  tableClassName = '',
  enableSmartSort = false,
  defaultSort = null
}) {
  const [sortState, setSortState] = useState(defaultSort);

  const sortableColumns = useMemo(
    () => columns.filter((col) => col.sortable !== false),
    [columns]
  );

  const sortedRows = useMemo(() => {
    if (!enableSmartSort || !sortState?.key || !rows.length) {
      return rows;
    }
    const column = columns.find((col) => col.key === sortState.key);
    if (!column) return rows;

    const dir = sortState.dir === 'desc' ? -1 : 1;
    return rows
      .map((row, idx) => ({ row, idx }))
      .sort((left, right) => {
        const leftRaw =
          typeof column.sortAccessor === 'function'
            ? column.sortAccessor(left.row)
            : left.row?.[column.key];
        const rightRaw =
          typeof column.sortAccessor === 'function'
            ? column.sortAccessor(right.row)
            : right.row?.[column.key];

        const result = compareValues(normalizeForSort(leftRaw), normalizeForSort(rightRaw)) * dir;
        if (result !== 0) return result;
        return left.idx - right.idx;
      })
      .map((entry) => entry.row);
  }, [enableSmartSort, sortState, rows, columns]);

  const toggleSort = (key) => {
    if (!enableSmartSort) return;
    setSortState((prev) => {
      if (!prev || prev.key !== key) {
        return { key, dir: 'asc' };
      }
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  if (loading) {
    return (
      <div className="hd-reports-panel">
        <div className="hd-reports-loading">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
          <span>{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (!sortedRows.length) {
    return (
      <div className="hd-reports-panel">
        <p className="hd-reports-empty mb-0">{emptyLabel}</p>
      </div>
    );
  }

  const max = maxValueKey ? Number(sortedRows[0]?.[maxValueKey] || 0) : 0;

  return (
    <div className="hd-reports-panel hd-reports-panel--table">
      <div className="hd-reports-table-wrap">
        <Table
          responsive
          hover
          bordered
          className={`hd-reports-table hd-reports-table--stats hd-reports-table--enhanced mb-0 ${tableClassName}`.trim()}
        >
        <thead>
          <tr>
            {columns.map((col) => {
              const isSortable = enableSmartSort && sortableColumns.some((c) => c.key === col.key);
              const isActive = sortState?.key === col.key;
              const direction = isActive ? sortState?.dir : null;
              return (
                <th key={col.key} className={col.className}>
                  {isSortable ? (
                    <button
                      type="button"
                      className={`hd-reports-sort-btn${isActive ? ' is-active' : ''}`}
                      onClick={() => toggleSort(col.key)}
                    >
                      <span>{col.label}</span>
                      <span className="hd-reports-sort-btn__dir" aria-hidden>
                        {direction === 'asc' ? 'ASC' : direction === 'desc' ? 'DESC' : 'SORT'}
                      </span>
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => (
            <tr key={row[rowKey] ?? idx}>
              {columns.map((col) => (
                <td key={col.key} className={col.className}>
                  {col.render
                    ? col.render(row, idx, max)
                    : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        </Table>
      </div>
    </div>
  );
}

export function statRankColumn(label, valueKey) {
  return {
    key: valueKey,
    label,
    render: (row, _idx, max) => <RankBar value={row[valueKey]} max={max} />
  };
}

export function statPercentColumn(label, valueKey = 'share_pct') {
  return {
    key: valueKey,
    label,
    className: 'text-end hd-reports-table__pct',
    render: (row) => (
      <span className="hd-reports-pct-badge">{formatPercentCell(row[valueKey])}</span>
    )
  };
}

export function statNumberColumn(label, valueKey) {
  return {
    key: valueKey,
    label,
    className: 'text-end',
    render: (row) => Number(row[valueKey] || 0).toLocaleString('fr-FR')
  };
}
